import React from 'react'
import { Box, Button, MenuItem, Select, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material'
import { Block, Check } from '@mui/icons-material'
import { useAppTranslations } from '../../../utils/hooks/useAppTranslations'
import { LocationTypesEnum } from '../../../pages/adgroup/settings/geoTargeting/GeoTargetingFields'

export type LocationTypeOption = { value: LocationTypesEnum; label: string }

type LocationsRuleBarProps = {
  exclusive: boolean
  /** Shows the Include / Exclude toggle */
  onExclusiveChange?: (exclusive: boolean) => void
  locationType: LocationTypesEnum
  /** Shows the location type select, with these options */
  locationTypeOptions?: LocationTypeOption[]
  onLocationTypeChange?: (locationType: LocationTypesEnum) => void
  onImportCsv?: () => void
  onClearAll?: () => void
}

const toggleButtonSx = { gap: 0.75, px: 1.5, textTransform: 'capitalize' } as const

/** Edit mode: the rule and the location type open the panel, next to the list actions */
const LocationsRuleBar: React.FC<LocationsRuleBarProps> = ({
  exclusive,
  onExclusiveChange,
  locationType,
  locationTypeOptions,
  onLocationTypeChange,
  onImportCsv,
  onClearAll,
}) => {
  const t = useAppTranslations()
  const showTypeSelect = Boolean(onLocationTypeChange && locationTypeOptions?.length)

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: 1.25,
        minHeight: 48,
        py: 1,
        pl: 1.5,
        pr: 1,
        boxSizing: 'border-box',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {onExclusiveChange && (
        <ToggleButtonGroup
          exclusive
          size="small"
          value={exclusive}
          onChange={(_, value: boolean | null) => {
            if (value !== null) onExclusiveChange(value)
          }}
          aria-label={t('adgroup.geoTargeting.rule')}
          sx={{ height: 32 }}
        >
          <ToggleButton value={false} color="primary" sx={toggleButtonSx}>
            <Check fontSize="small" />
            {t('commons.include')}
          </ToggleButton>
          <ToggleButton value={true} color="error" sx={toggleButtonSx}>
            <Block fontSize="small" />
            {t('commons.exclude')}
          </ToggleButton>
        </ToggleButtonGroup>
      )}
      {showTypeSelect && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="body2" color="textSecondary">
            {t('adgroup.geoTargeting.panel.byType')}
          </Typography>
          <Select
            size="small"
            value={locationType}
            onChange={(event) => onLocationTypeChange?.(event.target.value as LocationTypesEnum)}
            inputProps={{ 'aria-label': t('adgroup.geoTargeting.locationType') }}
            sx={{ height: 32, minWidth: 150, fontWeight: 500 }}
          >
            {locationTypeOptions?.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </Box>
      )}
      <Box sx={{ flex: 1 }} />
      {onImportCsv && (
        <Button size="small" variant="text" onClick={onImportCsv}>
          {t('adgroup.geoTargeting.importCSV')}
        </Button>
      )}
      {onClearAll && (
        <Button size="small" color="error" onClick={onClearAll}>
          {t('adgroup.geoTargeting.panel.clearAll')}
        </Button>
      )}
    </Box>
  )
}

export default LocationsRuleBar
